import { ILiveEvent } from '@features/live/api/liveApi.type';
import { ILiveSyncState, LiveSyncKind, LiveSyncMarks } from '@features/live/slice/liveSlice.type';
import { snapshotSince } from '@features/live/websocket/socketPolicy';

export const createLiveSync = (): ILiveSyncState => ({
  marks: {},
  eventSince: {},
});

export const markLive = (sync: ILiveSyncState, ids: number[], kinds: LiveSyncKind[], at: number) => {
  ids.forEach((id) => {
    const marks = sync.marks[id] ?? (sync.marks[id] = {});
    kinds.forEach((kind) => {
      marks[kind] = at;
    });
  });
};

export const requestSince = (sync: ILiveSyncState, startedAt: number) => snapshotSince(startedAt, sync.openedAt);

export const isNewer = (mark: number | undefined, since: number | undefined) =>
  mark !== undefined && since !== undefined && mark >= since;

const carriedBySocket = (sync: ILiveSyncState, mark: number | undefined) =>
  sync.openedAt !== undefined && mark !== undefined && mark >= sync.openedAt;

const latestLiveMark = (marks: LiveSyncMarks = {}) => {
  const times = [ marks.odds, marks.extra, marks.info ].filter((at): at is number => at !== undefined);
  return times.length ? Math.max(...times) : undefined;
};

export const mergeLiveEvent = (
  prev: ILiveEvent | undefined,
  next: ILiveEvent,
  sync: ILiveSyncState,
  since: number | undefined,
  base: ILiveEvent = next,
): ILiveEvent => {
  if (!prev) {
    return next;
  }
  const marks = sync.marks[next.id] ?? {};
  const merged: ILiveEvent = { ...base };
  if (isNewer(marks.odds, since)) {
    merged.count = prev.count;
    merged.mainlines = prev.mainlines;
  }
  if (prev.additional !== undefined && (
    isNewer(marks.extra, since) || (merged.additional === undefined && carriedBySocket(sync, marks.extra))
  )) {
    merged.additional = prev.additional;
  }
  if (isNewer(marks.lock, since)) {
    merged.lock = prev.lock;
  }
  if (isNewer(marks.show, since)) {
    merged.show = prev.show;
  }
  if (isNewer(marks.info, since)) {
    merged.infoLine = prev.infoLine;
  }
  return merged;
};

export const mergeLiveList = (
  prev: ILiveEvent[],
  incoming: ILiveEvent[],
  sync: ILiveSyncState,
  since: number | undefined,
): ILiveEvent[] => {
  const previous = new Map(prev.map((event) => [ event.id, event ]));
  const listed = new Set(incoming.map((event) => event.id));
  const merged = incoming.map((event) => mergeLiveEvent(previous.get(event.id), event, sync, since));
  prev.forEach((event) => {
    if (listed.has(event.id)) {
      return;
    }
    const marks = sync.marks[event.id];
    if (isNewer(latestLiveMark(marks), since) || (event.show && isNewer(marks?.show, since))) {
      merged.push(event);
    }
  });
  return merged;
};

export const pruneLiveSync = (sync: ILiveSyncState, events: ILiveEvent[]) => {
  const ids = new Set(events.map((event) => event.id));
  Object.keys(sync.marks).forEach((key) => {
    if (!ids.has(Number(key))) {
      delete sync.marks[Number(key)];
    }
  });
};
